/*
  Single.js - create invoice adjustment
*/

import React, { useState, useEffect, Fragment, useCallback } from 'react';
import {
  Alert,
  Button,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Grid,
  Stack,
  Box,
  InputLabel,
} from '@mui/material';

import { multiply, fixed } from '../../../utils/maths';

import BillingFormattedMessages from '../../../messages/billing/BillingFormattedMessages';

// NETWORKING
import useInvoiceRequest from '../../../requests/InvoiceRequest';

// COMPONENTS
import Loader from '../../common/Loader';
import HelpTooltip from '../../common/HelpTooltip';

export default function InvoiceAdjustment({ bill, errorHandler, backButton }) {
  const { getExtraData, createInvoiceAdjustment } = useInvoiceRequest();
  const [thisInvoice, setThisInvoice] = useState(false);
  const [edits, setEdits] = useState({ charge_quantity: 1, rate: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [isRefund, setIsRefund] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [defaultQuantity, setDefaultQuantity] = useState(1);
  const [salesTaxFlag, setSalesTaxFlag] = useState(null);
  const [taxRate, setTaxRate] = useState(null);
  const [customerType, setCustomerType] = useState(null);
  const [revenue, setRevenue] = useState(0);
  const [tax, setTax] = useState(0);
  const [creditCardSurcharge, setCreditCardSurcharge] = useState(0);
  const [total, setTotal] = useState(0);
  const [refundTotal, setRefundTotal] = useState(0);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [defaultRefundRevenue, setDefaultRefundRevenue] = useState(null);
  const [stripeRate, setStripeRate] = useState(0);

  useEffect(() => {
    async function fetchData() {
      if (bill && bill !== null) {
        let {
          sales_tax_flag,
          taxRate,
          customer_type,
          refundsForBill,
          stripeRate,
        } =
          (await getExtraData(bill.customer_id, bill.bill_id, errorHandler)) ||
          {};
        const invoiceTotal = fixed(Number(bill.amount), 2);
        let refundTotal = '';
        for (let i = 0; i < refundsForBill.length; i++) {
          refundTotal =
            Number(refundsForBill[i].charge_amount) + Number(refundTotal);
        }
        refundTotal = fixed(refundTotal, 2);
        setStripeRate(Number(stripeRate));
        setSalesTaxFlag(sales_tax_flag);
        setCustomerType(customer_type);
        setTaxRate(taxRate);
        setRefundTotal(refundTotal ? refundTotal : 0);
        setInvoiceTotal(invoiceTotal);
        setLoading(false);
      }
    }
    fetchData();
  }, [bill, errorHandler]);

  const handleToggle = (event) => {
    const value = event.target.value;
    if (value === 'refund') {
      const total = Number(invoiceTotal) + Number(refundTotal);
      let revenue;
      if (customerType === 'A') {
        // post-paid (revenue is tax-exclusive) so the tax has to be added on after.
        revenue = fixed(Number(total) / (1 + Number(taxRate)));
      } else if (customerType === 'B') {
        // pre-paid (revenue is tax-inclusive) so the tax is included in the revenue.
        revenue = fixed(Number(total));
      }
      revenue = -revenue;
      setEdits({
        charge_quantity: 1,
        general_desc: edits.general_desc,
        rate: revenue,
      });
      setDefaultRefundRevenue(revenue);
      setIsRefund(true);
      setThisInvoice(false);
      setHasError(false);
      handleChange({ target: { name: 'rate', value: revenue } });
    } else {
      setRevenue('');
      setTax('');
      setTotal('');
      setCreditCardSurcharge('');
      setEdits({
        charge_quantity: 1,
        general_desc: edits.general_desc,
      });
    }
    setThisInvoice(value === 'true' ? false : true);
    setIsRefund(false);
    setHasError(false);
  };

  const handleValueChanges = useCallback(() => {
    const { revenue, total, tax, creditCardSurcharge } = calculateRevenue(
      edits.rate,
      edits.charge_quantity
    );
    setRevenue(revenue);
    setTotal(total);
    setTax(tax);
    setCreditCardSurcharge(creditCardSurcharge);
  }, [edits]);

  useEffect(() => {
    handleValueChanges();
  }, [handleValueChanges]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setEdits((prevEdits) => ({
      ...prevEdits,
      [name]: value,
    }));
  };

  const validateNumber = (value, int) => {
    let regExPattern;
    if (int) {
      if (!value || value === '') return false;
      regExPattern = /^[1-9][0-9]*$/;
    } else {
      if (!value || value === '') return false;
      regExPattern = /^[+-]?\d+(\.\d+)?$/;
    }
    return regExPattern.test(value);
  };

  const validateRefundAmount = () => {
    if (salesTaxFlag === 'Y' && taxRate > 0) {
      if (Math.abs(total + refundTotal) > invoiceTotal) return false;
    } else if (Math.abs(revenue + refundTotal) > invoiceTotal) {
      return false;
    }
    return true;
  };

  const validateForm = (edits) => {
    let hasError = false;
    let errorText = '';
    if (!edits.general_desc || typeof edits.general_desc === 'undefined') {
      hasError = true;
      errorText = 'Requires a charge description.';
    } else if (!validateNumber(edits.charge_quantity, true)) {
      hasError = true;
      errorText = 'Quantity must be a positive number.';
    } else if (!validateNumber(edits.rate, false)) {
      hasError = true;
      errorText = 'Rate must be a non-zero number.';
    } else if (!isRefund && edits.rate < 0) {
      hasError = true;
      errorText = 'Adjustment rate must be a positive number.';
    } else if (isRefund && edits.rate >= 0) {
      hasError = true;
      errorText = 'Refund rate must be a negative number.';
    } else if (isRefund && !validateRefundAmount()) {
      hasError = true;
      errorText =
        'Invalid refund amount. The requested refund exceeds the refundable amount for this invoice';
    }
    return { hasError, errorText };
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setHasError(false);
    setErrorText('');
    if (!edits.charge_quantity && typeof edits.charge_quantity === 'undefined')
      edits.charge_quantity = defaultQuantity;
    const { hasError, errorText } = validateForm(edits);

    if (!hasError) {
      edits.charge_quantity = Number(edits.charge_quantity);
      edits.rate = Number(edits.rate);
      edits.charge_amount = Number(revenue);
      edits.applyToNextInvoice = thisInvoice;
      await createInvoiceAdjustment(
        bill.customer_id,
        bill.bill_id,
        edits,
        errorHandler
      );
      backButton(true);
    } else {
      setHasError(hasError);
      setErrorText(errorText);
      setIsSaving(false);
      return false;
    }
  };

  const generateErrorText = () =>
    hasError ? <Alert severity="error">{errorText}</Alert> : null;

  const calculateRevenue = (rate, quantity) => {
    let revenue = 0;
    let total = 0;
    let tax = 0;
    let creditCardSurcharge = 0;
    if (!quantity && typeof quantity === 'undefined') {
      quantity = defaultQuantity;
    }

    if (rate && quantity) {
      revenue = fixed(Number(rate) * Number(quantity), 2);
      tax = fixed(Number(revenue) * Number(taxRate), 2);
      if (customerType === 'A') {
        // post-paid (revenue is tax-exclusive) so the tax has to be added on after.
        total = fixed(Number(revenue) + Number(tax), 2);
      } else if (customerType === 'B') {
        // pre-paid (revenue is tax-inclusive) so the tax is included in the revenue.
        total = revenue;
      }
    }

    // Adds the credit card surcharge to the estimate
    if (stripeRate && total > 0) {
      creditCardSurcharge = fixed(
        multiply(Number(total), Number(stripeRate)),
        2
      );
      total = fixed(Number(total) + Number(creditCardSurcharge), 2);
      console.log(creditCardSurcharge);
    }
    return { revenue, total, tax, creditCardSurcharge };
  };

  const generateGSTDisplay = () => {
    const gstDisplay = (
      <Grid size={12}>
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            alignItems: { md: 'center' },
            gap: { md: 2 },
          }}
        >
          <InputLabel sx={{ minWidth: 200 }}>
            {BillingFormattedMessages.tax}
          </InputLabel>

          <TextField
            type="number"
            name="tax"
            value={tax}
            size="small"
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        </Stack>
      </Grid>
    );
    const totalDisplay = (
      <Grid size={12}>
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            alignItems: { md: 'center' },
            gap: { md: 2 },
          }}
        >
          <InputLabel sx={{ minWidth: 200 }}>
            {BillingFormattedMessages.total}
            <HelpTooltip
              message={
                <Typography>
                  The total also includes the stripe credit card surcharge for
                  Business CC customers
                </Typography>
              }
            />
            :
          </InputLabel>

          <TextField
            type="number"
            name="total"
            value={total}
            size="small"
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        </Stack>
      </Grid>
    );
    const surchargeDisplay = (
      <Grid size={12}>
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            alignItems: { md: 'center' },
            gap: { md: 2 },
          }}
        >
          <InputLabel sx={{ minWidth: 200 }}>Credit Card Surcharge:</InputLabel>

          <TextField
            type="number"
            name="tax"
            value={creditCardSurcharge}
            size="small"
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        </Stack>
      </Grid>
    );
    if (salesTaxFlag === 'Y' && taxRate > 0) {
      return (
        <Fragment>
          {gstDisplay}
          {!isRefund && stripeRate !== 0 && surchargeDisplay}
          {totalDisplay}
        </Fragment>
      );
    }
    if (!isRefund && stripeRate !== 0) {
      return (
        <Fragment>
          {surchargeDisplay}
          {totalDisplay}
        </Fragment>
      );
    }
    return null;
  };

  const generateRefundDisplay = () => (
    <>
      <Grid size={6}>
        <Typography fontWeight={700}>
          {BillingFormattedMessages.invoiceTotal}
        </Typography>
      </Grid>
      <Grid size={6}>{`${invoiceTotal} ${bill.plan_currency}`}</Grid>

      <Grid size={6}>
        <Typography fontWeight={700}>
          {BillingFormattedMessages.refundTotal}
        </Typography>
      </Grid>
      <Grid size={6}>{`${refundTotal} ${bill.plan_currency}`}</Grid>
    </>
  );

  const generateFormButtons = () => {
    return (
      <Box sx={{ display: 'flex', gap: 2, marginY: 4, justifyContent: 'end' }}>
        <Button variant="link" color="secondary" onClick={() => backButton()}>
          {BillingFormattedMessages.cancel}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving
            ? BillingFormattedMessages.saving
            : BillingFormattedMessages.save}
        </Button>
      </Box>
    );
  };

  if (loading) return <Loader />;

  return (
    <>
      <Grid container spacing={2} sx={{ maxWidth: 400 }}>
        <Grid size={6}>
          <Typography fontWeight={700}>
            {BillingFormattedMessages.customerName}
          </Typography>
        </Grid>
        <Grid size={6}>{`${bill.customer_name}`}</Grid>

        <Grid size={6}>
          <Typography fontWeight={700}>
            {BillingFormattedMessages.customerAccountNo}
          </Typography>
        </Grid>
        <Grid size={6}>{`${bill.account_no}`}</Grid>

        <Grid size={6}>
          <Typography fontWeight={700}>
            {BillingFormattedMessages.invoiceNumber}
          </Typography>
        </Grid>
        <Grid size={6}>{`${bill.invoice_number}`}</Grid>

        <Grid size={6}>
          <Typography fontWeight={700}>
            {BillingFormattedMessages.invoiceDueDate}
          </Typography>
        </Grid>
        <Grid size={6}>{`${bill.invoice_date}`}</Grid>
        {generateRefundDisplay()}
      </Grid>

      <Grid container sx={{ marginY: 2 }}>
        <Grid size={8}>
          <RadioGroup
            name="listToggle"
            defaultValue={true}
            onChange={handleToggle}
            sx={{ flexDirection: 'row', gap: 2 }}
          >
            <FormControlLabel
              value={true}
              control={<Radio />}
              label={BillingFormattedMessages.thisInvoice}
            />
            {customerType === 'A' && (
              <FormControlLabel
                value={false}
                control={<Radio />}
                label={BillingFormattedMessages.nextInvoice}
              />
            )}
            {bill.billing_status.toLowerCase() === 'paid' && (
              <FormControlLabel
                value={'refund'}
                control={<Radio />}
                label={BillingFormattedMessages.refundNow}
              />
            )}
          </RadioGroup>
        </Grid>
      </Grid>

      <form>
        <Grid container spacing={2} maxWidth={{ md: 500 }}>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {BillingFormattedMessages.charge}
              </InputLabel>
              <TextField
                type="text"
                name="general_desc"
                value={edits.general_desc || ''}
                onChange={handleChange}
                size="small"
                fullWidth
              />
            </Stack>
          </Grid>
          {!isRefund && (
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>
                  {BillingFormattedMessages.quantity}
                </InputLabel>
                <TextField
                  type="number"
                  name="charge_quantity"
                  value={edits.charge_quantity}
                  onChange={handleChange}
                  size="small"
                  fullWidth
                />
              </Stack>
            </Grid>
          )}
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {BillingFormattedMessages.rate}
              </InputLabel>
              <TextField
                type="number"
                name="rate"
                value={edits.rate}
                onChange={handleChange}
                size="small"
                fullWidth
              />
            </Stack>
          </Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {BillingFormattedMessages.revenue}
              </InputLabel>

              <TextField
                type="number"
                name="charge_amount"
                value={revenue}
                size="small"
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
              />
            </Stack>
          </Grid>
          {generateGSTDisplay()}

          <Grid size={12}>
            {generateErrorText()}
            {generateFormButtons()}
          </Grid>
        </Grid>
      </form>
    </>
  );
}
